/**
 * 2FA self-service + login-challenge API.
 *
 * Backed by the /auth/2fa/* endpoints. The setup response includes a
 * base64 PNG so the SPA can render the QR inline without a JS QR
 * library.
 */
import client from "./client";
import type { TokenResponse } from "@/types/auth";

export interface TwoFactorSetupResponse {
  secret: string;
  otpauth_uri: string;
  /** Base64-encoded PNG. ``null`` when the backend qrcode lib failed
   *  to load — the SPA falls back to a raw URI display. */
  qr_png_base64: string | null;
}

export interface TwoFactorVerifyResponse {
  backup_codes: string[];
}

export interface TwoFactorStatus {
  enabled: boolean;
  backup_codes_remaining: number;
}

export interface LoginRequires2FA {
  requires_2fa: true;
  temp_token: string;
  expires_in: number;
}

export type LoginResponse = TokenResponse | LoginRequires2FA;

export function isLoginRequires2FA(
  res: LoginResponse,
): res is LoginRequires2FA {
  return (res as LoginRequires2FA).requires_2fa === true;
}

export async function setup2FA(): Promise<TwoFactorSetupResponse> {
  const res = await client.post<TwoFactorSetupResponse>("/auth/2fa/setup");
  return res.data;
}

export async function verify2FA(
  code: string,
): Promise<TwoFactorVerifyResponse> {
  const res = await client.post<TwoFactorVerifyResponse>(
    "/auth/2fa/verify",
    { code },
  );
  return res.data;
}

export async function disable2FA(password: string): Promise<void> {
  await client.post("/auth/2fa/disable", { password });
}

export async function get2FAStatus(): Promise<TwoFactorStatus> {
  const res = await client.get<TwoFactorStatus>("/auth/2fa/status");
  return res.data;
}

export async function regenerate2FABackupCodes(
  password: string,
): Promise<TwoFactorVerifyResponse> {
  const res = await client.post<TwoFactorVerifyResponse>(
    "/auth/2fa/regenerate-backup-codes",
    { password },
  );
  return res.data;
}

export async function challenge2FA(
  tempToken: string,
  code: string,
): Promise<TokenResponse> {
  const res = await client.post<TokenResponse>("/auth/2fa/challenge", {
    temp_token: tempToken,
    code,
  });
  return res.data;
}
